<template>
  <div class="page-container">
    <!-- 优惠券信息 -->
    <el-card v-loading="pageLoading" class="coupon-info-card">
      <div slot="header" class="page-header">
        <span class="page-title">发放优惠券</span>
        <div class="page-actions">
          <el-button size="small" icon="el-icon-arrow-left" @click="$router.push('/coupon/index')">返回</el-button>
        </div>
      </div>

      <template v-if="coupon">
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="优惠券名称">
            <span class="bold">{{ coupon.name }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="立减金额">
            <span class="val-primary">¥{{ Number(coupon.amount).toFixed(2) }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="coupon.status === 1 ? 'success' : 'danger'" size="mini">
              {{ coupon.status === 1 ? '启用' : '已禁用' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="可用产品">
            <span v-if="coupon.product">{{ coupon.product.name }}</span>
            <span v-else class="text-muted">不限产品</span>
          </el-descriptions-item>
          <el-descriptions-item label="有效期" :span="2">
            {{ coupon.started_at }} ~ {{ coupon.expired_at }}
          </el-descriptions-item>
        </el-descriptions>
      </template>
    </el-card>

    <!-- 已选客户区域 -->
    <el-card class="user-select-card">
      <div slot="header" class="section-header">
        <span class="section-title">
          已选客户
          <el-badge v-if="selectedUsers.length" :value="selectedUsers.length" type="primary" class="count-badge" />
        </span>
        <div class="header-btns">
          <el-button
            type="primary" size="small" icon="el-icon-plus"
            @click="openUserDialog"
          >添加客户</el-button>
          <el-button
            type="warning" size="small" icon="el-icon-shopping-bag-1"
            @click="openOrderBasedDialog"
          >根据产品订单添加客户</el-button>
        </div>
      </div>

      <!-- 已选客户标签 -->
      <div v-if="selectedUsers.length" class="selected-tags">
        <el-tag
          v-for="u in selectedUsers"
          :key="u.id"
          closable
          type="info"
          size="medium"
          class="user-tag"
          @close="removeUser(u.id)"
        >
          <span class="tag-label">{{ userLabel(u) }}</span>
          <span class="tag-qty"> × {{ u.quantity }}张</span>
        </el-tag>
      </div>
      <div v-else class="empty-tip">
        <i class="el-icon-user" />
        <span>暂未选择客户，点击「添加客户」按钮选择</span>
      </div>

      <!-- 提交 -->
      <div class="submit-bar">
        <el-button
          type="primary"
          :loading="issuing"
          :disabled="!selectedUsers.length || !coupon || coupon.status !== 1"
          @click="handleIssue"
        >
          确认发放（{{ selectedUsers.length }} 人）
        </el-button>
        <span v-if="coupon && coupon.status !== 1" class="warn-tip">
          <i class="el-icon-warning-outline" /> 该优惠券已禁用，无法发放
        </span>
      </div>
    </el-card>

    <!-- 客户选择弹窗 -->
    <el-dialog
      title="选择客户"
      :visible.sync="userDialog.visible"
      width="920px"
      @open="fetchUserList"
      @close="resetUserDialog"
    >
      <!-- 搜索 -->
      <div class="dialog-search">
        <el-input
          v-model="userDialog.keyword"
          placeholder="搜索手机号/姓名"
          clearable
          style="width:220px"
          @keyup.enter.native="searchUsers"
        />
        <el-button type="primary" size="mini" icon="el-icon-search" @click="searchUsers">查询</el-button>
        <el-button size="mini" @click="userDialog.keyword = ''; fetchUserList()">重置</el-button>
        <span class="dialog-selected-count">
          本次已勾选 <strong>{{ dialogChecked.length }}</strong> 人
        </span>
      </div>

      <!-- 客户表格 -->
      <el-table
        ref="userTable"
        :data="userDialog.list"
        v-loading="userDialog.loading"
        border
        stripe
        style="width:100%;margin-top:10px"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="46" :selectable="row => !isAlreadySelected(row.id)" />
        <el-table-column prop="id" label="ID" width="60" align="center" />
        <el-table-column label="手机号" width="130">
          <template slot-scope="{ row }">{{ row.phone || '未绑定' }}</template>
        </el-table-column>
        <el-table-column prop="nickname" label="姓名" min-width="100" show-overflow-tooltip />
        <el-table-column label="状态" width="70" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="mini">
              {{ row.status === 1 ? '正常' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="已选" width="55" align="center">
          <template slot-scope="{ row }">
            <i v-if="isAlreadySelected(row.id)" class="el-icon-check already-icon" />
          </template>
        </el-table-column>
        <!-- 操作列 -->
        <el-table-column label="操作" width="110" align="center">
          <template slot-scope="{ row }">
            <el-button
              type="primary" size="mini" icon="el-icon-shopping-bag-1"
              @click="openUserOrderDialog(row)"
            >产品订单</el-button>
          </template>
        </el-table-column>
        <!-- 发放数量列（最后一列） -->
        <el-table-column label="发放数量" width="130" align="center">
          <template slot-scope="{ row }">
            <el-input-number
              :value="getQuantity(row.id)"
              :min="1"
              :max="100"
              :step="1"
              size="mini"
              controls-position="right"
              style="width:100px"
              @change="val => setQuantity(row.id, val)"
            />
          </template>
        </el-table-column>
      </el-table>

      <!-- 弹窗分页 -->
      <el-pagination
        :current-page="userDialog.page"
        :page-sizes="[10, 15, 20]"
        :page-size="userDialog.pageSize"
        :total="userDialog.total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top:14px;text-align:right"
        @size-change="val => { userDialog.pageSize = val; userDialog.page = 1; fetchUserList() }"
        @current-change="val => { userDialog.page = val; fetchUserList() }"
      />

      <div slot="footer">
        <el-button size="small" @click="userDialog.visible = false">取消</el-button>
        <el-button type="primary" size="small" @click="confirmAddUsers">
          确定添加（{{ dialogChecked.length }} 人）
        </el-button>
      </div>
    </el-dialog>

    <!-- 客户产品订单弹窗（嵌套，append-to-body） -->
    <el-dialog
      :title="`${userOrderDialog.username} 的产品订单`"
      :visible.sync="userOrderDialog.visible"
      width="920px"
      append-to-body
      @close="resetUserOrderDialog"
    >
      <div class="dialog-search">
        <el-input
          v-model="userOrderDialog.keyword"
          placeholder="搜索订单号"
          clearable
          style="width:200px"
          @keyup.enter.native="searchUserOrders"
        />
        <el-select v-model="userOrderDialog.status" placeholder="订单状态" clearable style="width:120px">
          <el-option label="待付款" :value="0" />
          <el-option label="已付款" :value="1" />
          <el-option label="部分退款" :value="2" />
          <el-option label="已取消" :value="3" />
          <el-option label="已退款" :value="4" />
          <el-option label="已完成" :value="5" />
        </el-select>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="searchUserOrders">查询</el-button>
        <el-button size="mini" @click="userOrderDialog.keyword = ''; userOrderDialog.status = ''; searchUserOrders()">重置</el-button>
      </div>
      <el-table
        :data="userOrderDialog.list"
        v-loading="userOrderDialog.loading"
        border
        stripe
        style="width:100%;margin-top:10px"
      >
        <el-table-column prop="order_no" label="订单号" min-width="180" show-overflow-tooltip />
        <el-table-column prop="product_name" label="产品名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="quantity" label="数量" width="70" align="center" />
        <el-table-column label="实付金额" width="110" align="right">
          <template slot-scope="{ row }">
            <span class="price-text">¥{{ Number(row.pay_amount).toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="productOrderStatusType(row.status)" size="small">
              {{ row.status_label || productOrderStatusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="下单时间" width="160" />
      </el-table>
      <el-pagination
        :current-page="userOrderDialog.page"
        :page-sizes="[10, 15, 20]"
        :page-size="userOrderDialog.pageSize"
        :total="userOrderDialog.total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top:14px;text-align:right"
        @size-change="val => { userOrderDialog.pageSize = val; userOrderDialog.page = 1; fetchUserOrders() }"
        @current-change="val => { userOrderDialog.page = val; fetchUserOrders() }"
      />
    </el-dialog>

    <!-- 根据产品订单添加客户弹窗 -->
    <el-dialog
      title="根据产品订单添加客户（已完成订单）"
      :visible.sync="orderBasedDialog.visible"
      width="1060px"
      @close="resetOrderBasedDialog"
    >
      <div class="dialog-search">
        <el-input
          v-model="orderBasedDialog.keyword"
          placeholder="搜索产品订单号"
          clearable
          style="width:220px"
          @keyup.enter.native="searchOrderBased"
        />
        <el-button type="primary" size="mini" icon="el-icon-search" @click="searchOrderBased">查询</el-button>
        <el-button size="mini" @click="orderBasedDialog.keyword = ''; searchOrderBased()">重置</el-button>
        <span class="dialog-selected-count">
          本次已勾选 <strong>{{ orderBasedDialog.checked.length }}</strong> 条
        </span>
      </div>

      <el-table
        ref="orderBasedTable"
        :data="orderBasedDialog.list"
        v-loading="orderBasedDialog.loading"
        border
        stripe
        style="width:100%;margin-top:10px"
        @selection-change="val => orderBasedDialog.checked = val"
      >
        <el-table-column type="selection" width="46" />
        <el-table-column label="姓名" width="110" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.buyer_name || '—' }}</template>
        </el-table-column>
        <el-table-column label="手机号" width="130">
          <template slot-scope="{ row }">{{ row.buyer_phone || '—' }}</template>
        </el-table-column>
        <el-table-column prop="order_no" label="产品订单号" min-width="180" show-overflow-tooltip />
        <el-table-column prop="product_name" label="产品名称" min-width="130" show-overflow-tooltip />
        <el-table-column label="产品金额" width="100" align="right">
          <template slot-scope="{ row }">
            <span class="price-text">¥{{ Number(row.pay_amount).toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="服务订单数量" width="80" align="center">
          <template slot-scope="{ row }">{{ row.quantity }}</template>
        </el-table-column>
        <el-table-column label="完成时间" width="155">
          <template slot-scope="{ row }">{{ row.completed_at || '—' }}</template>
        </el-table-column>
        <el-table-column label="已选" width="52" align="center">
          <template slot-scope="{ row }">
            <i v-if="isOrderBuyerSelected(row)" class="el-icon-check already-icon" />
          </template>
        </el-table-column>
        <el-table-column label="发放数量" width="130" align="center">
          <template slot-scope="{ row }">
            <el-input-number
              :value="getOrderCouponQty(row)"
              :min="1"
              :max="100"
              :step="1"
              size="mini"
              controls-position="right"
              style="width:100px"
              @change="val => setOrderCouponQty(row, val)"
            />
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        :current-page="orderBasedDialog.page"
        :page-sizes="[10, 15, 20]"
        :page-size="orderBasedDialog.pageSize"
        :total="orderBasedDialog.total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top:14px;text-align:right"
        @size-change="val => { orderBasedDialog.pageSize = val; orderBasedDialog.page = 1; fetchOrderBasedList() }"
        @current-change="val => { orderBasedDialog.page = val; fetchOrderBasedList() }"
      />

      <div slot="footer">
        <el-button size="small" @click="orderBasedDialog.visible = false">取消</el-button>
        <el-button type="primary" size="small" @click="confirmAddFromOrders">
          确定添加（{{ orderBasedDialog.checked.length }} 条）
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCouponDetail, issueCoupon } from '@/api/coupon'
import { getMemberList } from '@/api/member'
import { getUserProductOrders, getProductOrderList } from '@/api/order'

export default {
  name: 'CouponIssue',
  data() {
    return {
      pageLoading: false,
      issuing: false,
      coupon: null,
      selectedUsers: [],

      userDialog: {
        visible: false,
        loading: false,
        keyword: '',
        list: [],
        page: 1,
        pageSize: 15,
        total: 0,
        quantities: {}   // { userId: quantity }
      },
      dialogChecked: [],

      // 根据产品订单添加客户弹窗
      orderBasedDialog: {
        visible: false,
        loading: false,
        keyword: '',
        list: [],
        total: 0,
        page: 1,
        pageSize: 15,
        checked: [],
        quantities: {}   // { orderId: quantity }
      },

      // 客户产品订单弹窗（嵌套）
      userOrderDialog: {
        visible: false,
        loading: false,
        userId: null,
        username: '',
        keyword: '',
        status: '',
        list: [],
        total: 0,
        page: 1,
        pageSize: 15
      }
    }
  },
  computed: {
    couponId() {
      return this.$route.query.id ? Number(this.$route.query.id) : null
    },
    selectedUserIds() {
      return new Set(this.selectedUsers.map(u => u.id))
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (to.path !== '/coupon/issue') return
        if (to.query.id) {
          this.fetchCoupon()
        } else {
          this.$message.error('缺少优惠券ID')
          this.$router.push('/coupon/index')
        }
      }
    }
  },
  methods: {
    async fetchCoupon() {
      this.pageLoading = true
      try {
        const res = await getCouponDetail({ id: this.couponId })
        this.coupon = res.data
      } finally {
        this.pageLoading = false
      }
    },

    // ── 客户弹窗 ────────────────────────────────────────────
    openUserDialog() {
      this.userDialog.visible = true
    },

    resetUserDialog() {
      this.dialogChecked = []
      this.userDialog.keyword = ''
      this.userDialog.page = 1
      this.userDialog.quantities = {}
      this.$nextTick(() => {
        this.$refs.userTable && this.$refs.userTable.clearSelection()
      })
    },

    searchUsers() {
      this.userDialog.page = 1
      this.fetchUserList()
    },

    async fetchUserList() {
      this.userDialog.loading = true
      try {
        const res = await getMemberList({
          keyword:  this.userDialog.keyword,
          page:     this.userDialog.page,
          pageSize: this.userDialog.pageSize
        })
        const d = res.data || {}
        this.userDialog.list  = d.list || []
        this.userDialog.total = d.total || 0
      } finally {
        this.userDialog.loading = false
      }
    },

    getQuantity(userId) {
      return this.userDialog.quantities[userId] || 1
    },
    setQuantity(userId, val) {
      this.$set(this.userDialog.quantities, userId, val || 1)
    },

    handleSelectionChange(rows) {
      this.dialogChecked = rows
    },

    isAlreadySelected(id) {
      return this.selectedUserIds.has(id)
    },

    confirmAddUsers() {
      if (!this.dialogChecked.length) {
        this.$message.warning('请先勾选客户')
        return
      }
      let added = 0, merged = 0
      this.dialogChecked.forEach(u => {
        const qty = this.getQuantity(u.id)
        const existing = this.selectedUsers.find(s => s.id === u.id)
        if (existing) {
          existing.quantity += qty
          merged++
        } else {
          this.selectedUsers.push({ ...u, quantity: qty })
          added++
        }
      })
      const parts = []
      if (added) parts.push(`新增 ${added} 位客户`)
      if (merged) parts.push(`${merged} 位已存在客户发放数量已累加`)
      this.$message.success(parts.join('，') || '操作完成')
      this.userDialog.visible = false
    },

    userLabel(u) {
      const name = u.nickname || u.username || ''
      if (u.phone && name && name !== u.phone) return `${u.phone}(${name})`
      if (u.phone) return u.phone
      return name || String(u.id)
    },

    removeUser(id) {
      this.selectedUsers = this.selectedUsers.filter(u => u.id !== id)
    },

    // ── 客户产品订单（嵌套弹窗） ─────────────────────────────
    openUserOrderDialog(row) {
      this.userOrderDialog.userId   = row.id
      this.userOrderDialog.username = row.nickname || row.username || row.phone
      this.userOrderDialog.keyword  = ''
      this.userOrderDialog.status   = ''
      this.userOrderDialog.page     = 1
      this.userOrderDialog.pageSize = 15
      this.userOrderDialog.visible  = true
      this.fetchUserOrders()
    },

    resetUserOrderDialog() {
      this.userOrderDialog.list  = []
      this.userOrderDialog.total = 0
      this.userOrderDialog.page  = 1
    },

    searchUserOrders() {
      this.userOrderDialog.page = 1
      this.fetchUserOrders()
    },

    async fetchUserOrders() {
      this.userOrderDialog.loading = true
      try {
        const params = {
          user_id:  this.userOrderDialog.userId,
          page:     this.userOrderDialog.page,
          pageSize: this.userOrderDialog.pageSize
        }
        if (this.userOrderDialog.keyword) params.order_no = this.userOrderDialog.keyword
        if (this.userOrderDialog.status !== '') params.status = this.userOrderDialog.status
        const res = await getUserProductOrders(params)
        const d = res.data || {}
        this.userOrderDialog.list  = d.list || []
        this.userOrderDialog.total = d.total || 0
      } finally {
        this.userOrderDialog.loading = false
      }
    },

    productOrderStatusLabel(s) {
      return { 0: '待付款', 1: '已付款', 2: '部分退款', 3: '已取消', 4: '已退款', 5: '已完成' }[s] ?? String(s)
    },
    productOrderStatusType(s) {
      return { 0: 'info', 1: 'success', 2: 'warning', 3: '', 4: 'danger', 5: 'success' }[s] ?? ''
    },

    // ── 根据产品订单添加客户 ─────────────────────────────────
    openOrderBasedDialog() {
      this.orderBasedDialog.keyword   = ''
      this.orderBasedDialog.page      = 1
      this.orderBasedDialog.pageSize  = 15
      this.orderBasedDialog.checked   = []
      this.orderBasedDialog.quantities = {}
      this.orderBasedDialog.visible   = true
      this.fetchOrderBasedList()
    },

    resetOrderBasedDialog() {
      this.orderBasedDialog.list  = []
      this.orderBasedDialog.total = 0
      this.orderBasedDialog.page  = 1
      this.orderBasedDialog.checked = []
    },

    searchOrderBased() {
      this.orderBasedDialog.page = 1
      this.fetchOrderBasedList()
    },

    async fetchOrderBasedList() {
      this.orderBasedDialog.loading = true
      try {
        const params = {
          status:   5,   // 固定为已完成
          page:     this.orderBasedDialog.page,
          pageSize: this.orderBasedDialog.pageSize
        }
        if (this.orderBasedDialog.keyword) params.keywords = this.orderBasedDialog.keyword
        const res = await getProductOrderList(params)
        const d = res.data || {}
        this.orderBasedDialog.list  = d.list || []
        this.orderBasedDialog.total = d.total || 0
      } finally {
        this.orderBasedDialog.loading = false
      }
    },

    // 发放数量默认取订单产品数量
    getOrderCouponQty(row) {
      return this.orderBasedDialog.quantities[row.id] != null
        ? this.orderBasedDialog.quantities[row.id]
        : Math.min(row.quantity, 100)
    },
    setOrderCouponQty(row, val) {
      this.$set(this.orderBasedDialog.quantities, row.id, val || 1)
    },

    // 判断该订单购买人是否已在 selectedUsers 中
    isOrderBuyerSelected(row) {
      return this.selectedUsers.some(u => u.id === row.buyer_id)
    },

    confirmAddFromOrders() {
      if (!this.orderBasedDialog.checked.length) {
        this.$message.warning('请先勾选订单')
        return
      }
      // 先将本批次同一买家的数量合并
      const batchMap = {}
      this.orderBasedDialog.checked.forEach(row => {
        const id = row.buyer_id
        if (!batchMap[id]) {
          batchMap[id] = {
            id:       id,
            username: row.buyer_name || row.buyer_phone,
            phone:    row.buyer_phone,
            quantity: 0
          }
        }
        batchMap[id].quantity += this.getOrderCouponQty(row)
      })

      let added = 0, merged = 0
      Object.values(batchMap).forEach(entry => {
        const existing = this.selectedUsers.find(u => u.id === entry.id)
        if (existing) {
          existing.quantity += entry.quantity
          merged++
        } else {
          this.selectedUsers.push({ ...entry })
          added++
        }
      })

      const parts = []
      if (added) parts.push(`新增 ${added} 位客户`)
      if (merged) parts.push(`${merged} 位已存在客户发放数量已累加`)
      this.$message.success(parts.join('，'))
      this.orderBasedDialog.visible = false
    },

    // ── 发放 ────────────────────────────────────────────────
    async handleIssue() {
      if (!this.selectedUsers.length) {
        this.$message.warning('请先选择客户')
        return
      }
      try {
        await this.$confirm(
          `确认向 ${this.selectedUsers.length} 位客户发放优惠券「${this.coupon.name}」？`,
          '发放确认',
          { type: 'warning', confirmButtonText: '确认发放' }
        )
      } catch { return }

      this.issuing = true
      try {
        await issueCoupon({
          coupon_id: this.couponId,
          users: this.selectedUsers.map(u => ({
            user_id:  u.id,
            quantity: u.quantity || 1
          }))
        })
        this.$message.success(`成功向 ${this.selectedUsers.length} 位客户发放优惠券`)
        this.selectedUsers = []
        this.fetchCoupon()
      } finally {
        this.issuing = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .page-title { font-size: 15px; font-weight: 600; color: #303133; }
  .page-actions { display: flex; gap: 8px; }
}

.coupon-info-card { margin-bottom: 16px; }

.user-select-card {
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .count-badge { margin-left: 4px; }
    .header-btns { display: flex; gap: 8px; flex-wrap: wrap; }
  }
}

/* 已选客户标签区 */
.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 40px;
  padding: 4px 0;
}

.user-tag {
  height: 30px;
  line-height: 28px;
  padding: 0 10px;

  .tag-label { font-weight: 500; color: #303133; }
  .tag-qty   { margin-left: 6px; font-size: 12px; color: #409eff; font-weight: 600; }
}

.empty-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #c0c4cc;
  font-size: 13px;
  padding: 20px 0;
  i { font-size: 18px; }
}

.submit-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  .warn-tip { color: #e6a23c; font-size: 13px; i { margin-right: 4px; } }
}

/* 弹窗 */
.dialog-search {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  .dialog-selected-count {
    margin-left: auto;
    font-size: 13px;
    color: #606266;
    strong { color: #409eff; }
  }
}

.already-icon { color: #67c23a; font-size: 16px; font-weight: 700; }
.price-text   { color: #f56c6c; font-weight: 600; }
.text-muted   { color: #c0c4cc; font-size: 12px; }
.val-primary  { color: #409eff; font-weight: 600; }
.bold         { font-weight: 600; }
</style>
